import { useEffect, useRef, useState } from 'react';
import { fetchOrderSummary, type CheckoutOrderSummary } from './api';
import type { FunnelCompletion } from './external-funnel';
import { PaymentConfirmation, corePaymentMethodLabel, chargeStatusLabel, totalLabelFromLines, type ConfirmationLine } from './PaymentConfirmation';
import { formatMoney, periodSuffix } from './money';
import { checkoutI18n, currentLocale, normalizeLocale, tr, useCurrentLocale } from '../i18n';

export function FunnelConfirmation({ completion }: { completion: FunnelCompletion | null }) {
  useCurrentLocale();
  const [summary, setSummary] = useState<CheckoutOrderSummary | null>(null);
  const [loading, setLoading] = useState(completion !== null);
  const [attempt, setAttempt] = useState(0);
  // Reuse the same request during StrictMode's effect replay: the BFF consumes
  // the terminal session after delivering the summary.
  const request = useRef<{ key: string; promise: Promise<CheckoutOrderSummary | null> } | null>(null);

  useEffect(() => {
    const previousLocale = currentLocale();
    return () => { void checkoutI18n.changeLanguage(previousLocale); };
  }, []);

  useEffect(() => {
    if (completion === null) return;
    let active = true;
    setLoading(true);
    const key = JSON.stringify([completion, attempt]);
    if (request.current?.key !== key) {
      request.current = { key, promise: fetchOrderSummary(completion.checkoutId, completion.saleId, completion.handoffId) };
    }
    request.current.promise.then((result) => {
      if (active) {
        const locale = normalizeLocale(result?.buyerLocale);
        if (locale) void checkoutI18n.changeLanguage(locale);
        setSummary(result);
      }
    }).catch(() => {
      if (active) setSummary(null);
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [completion, attempt]);

  if (loading) return <main className="vp-shell vp-shell--center"><div className="vp-spinner" role="status" aria-label={tr('summary.loading')} /></main>;
  if (summary === null) {
    return <main className="vp-shell vp-shell--center"><section className="vp-card vp-success">
      <h1>{tr('summary.unavailableTitle')}</h1>
      <p>{tr('summary.unavailableBody')}</p>
      {completion !== null && <button className="vp-cta" type="button" onClick={() => setAttempt((value) => value + 1)}>{tr('error.retry')}</button>}
    </section></main>;
  }

  const lines: ConfirmationLine[] = summary.charges.map((charge) => ({
    id: charge.saleId,
    couponCode: charge.couponCode,
    coupon: charge.coupon,
    title: charge.title ?? tr(charge.kind === 'principal' ? 'summary.mainPurchase' : 'summary.additionalOffer'),
    amount: charge.amountCharged,
    amountLabel: formatMoney(charge.amountCharged),
    statusLabel: chargeStatusLabel(charge.status),
    categoryLabel: charge.recurring?.length ? tr('subscription.single') : tr('subscription.oneTime'),
    renewalLabel: charge.status === 'PAID' ? charge.recurring?.map((item) => `${formatMoney(item.amount)}${periodSuffix(item.billingPeriodUnit, item.billingPeriodCount)}`).join(' + ') : undefined,
    items: charge.items && charge.items.length > 1 ? charge.items.map((item) => ({ ...item, amountLabel: formatMoney(item.amount) })) : undefined,
    methodLabel: [corePaymentMethodLabel(charge.paymentMethod), charge.cardBrand && charge.cardLast4 ? tr('payment.cardEnding', { brand: charge.cardBrand, last4: charge.cardLast4 }) : undefined].filter(Boolean).join(' · '),
    paid: charge.status === 'PAID',
  }));
  const recurring = summary.charges.filter((charge) => charge.status === 'PAID').flatMap((charge) => charge.recurring ?? []).map((item) => ({
    id: item.id, title: item.title, amount: item.amount, amountLabel: formatMoney(item.amount),
    statusLabel: tr('checkout.subscriptionActive'),
    renewalLabel: `${formatMoney(item.amount)}${periodSuffix(item.billingPeriodUnit, item.billingPeriodCount)}`,
  }));
  const paid = lines.filter((line) => line.paid);
  return <main className="vp-shell vp-shell--center"><PaymentConfirmation
    email={summary.buyerEmail}
    paymentChanged={summary.charges.some(charge => ['REFUND_REQUESTED', 'PARTIALLY_REFUNDED', 'REFUNDED', 'CHARGED_BACK'].includes(charge.status))}
    currentPaymentLabel={corePaymentMethodLabel(summary.charges.find((charge) => charge.kind === 'principal')?.paymentMethod) ?? tr('common.unavailable')}
    paidNowLines={paid}
    paidNowDisplayLabel={summary.totalCaptured ? formatMoney(summary.totalCaptured) : totalLabelFromLines(paid) ?? tr('common.unavailable')}
    pendingConfirmationLines={lines.filter((line) => !line.paid)}
    recurringLines={recurring}
  /></main>;
}
